import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Tables } from '@/integrations/supabase/types';

type Conversation = Tables<'conversations'>;
type Message = Tables<'messages'>;

export function useConversations(projectId?: string, fetchAll?: boolean) {
  return useQuery<Conversation[]>({
    queryKey: ['conversations', fetchAll ? 'all' : (projectId ?? 'no-project')],
    queryFn: async () => {
      let query = supabase
        .from('conversations')
        .select('*')
        .order('updated_at', { ascending: false });

      if (!fetchAll) {
        if (projectId) {
          query = query.eq('project_id', projectId);
        } else {
          query = query.is('project_id', null);
        }
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });
}

export function useConversationMessages(conversationId: string | null) {
  return useQuery<Message[]>({
    queryKey: ['messages', conversationId],
    enabled: !!conversationId,
    // Streaming concorrente: a sessão viva (registry) é a fonte de verdade
    // durante a Geração; a invalidação no fim da Geração força o refresh por
    // evento. Por isso afrouxamos o cache: trocar de conversa dentro de 1min
    // não refetcha (instantâneo) e navegar por muitas conversas as mantém em
    // cache por 30min. Corta carga no Supabase sem perder frescor por evento.
    staleTime: 60_000,
    gcTime: 30 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId!)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data;
    },
  });
}

export function useCreateConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { title?: string; model: string; project_id?: string | null; agent_id?: string | null; scope?: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Não autenticado');

      const scope = input.scope ?? (input.project_id ? 'project' : 'personal');

      const { data, error } = await supabase
        .from('conversations')
        .insert({
          user_id: user.id,
          title: input.title ?? 'Nova conversa',
          model: input.model,
          project_id: input.project_id ?? null,
          agent_id: input.agent_id ?? null,
          scope,
        } as any)
        .select()
        .single();
      if (error) throw error;
      return data as Conversation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

export function useRenameConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, title }: { id: string; title: string }) => {
      const { error } = await supabase
        .from('conversations')
        .update({ title })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

export function useDeleteConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('conversations')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

export function useForkConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ conversationId, forkAtMessageId }: { conversationId: string; forkAtMessageId: string }) => {
      // a. Fetch all messages ordered by created_at ASC
      const { data: allMessages, error: msgError } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });
      if (msgError) throw msgError;

      // b. Keep messages up to and including forkAtMessageId
      const cutIndex = allMessages.findIndex((m) => m.id === forkAtMessageId);
      if (cutIndex === -1) throw new Error('Mensagem de fork não encontrada');
      const messagesToCopy = allMessages.slice(0, cutIndex + 1);

      // c. Fetch original conversation
      const { data: original, error: convError } = await supabase
        .from('conversations')
        .select('*')
        .eq('id', conversationId)
        .single();
      if (convError) throw convError;

      // d. Create forked conversation
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Não autenticado');

      // Snapshot original owner's display name
      const { data: ownerProfile } = await supabase
        .from('profiles')
        .select('nome, email')
        .eq('id', original.user_id)
        .maybeSingle();
      const ownerName = ownerProfile?.nome || ownerProfile?.email || null;

      const { data: forked, error: forkError } = await supabase
        .from('conversations')
        .insert({
          user_id: user.id,
          title: `Fork: ${original.title.slice(0, 80)}`,
          model: original.model,
          project_id: original.project_id,
          agent_id: original.agent_id,
          scope: (original as any).scope ?? (original.project_id ? 'project' : 'personal'),
          forked_from_conversation_id: conversationId,
          forked_at_message_id: forkAtMessageId,
          forked_from_title: original.title,
          forked_from_owner_name: ownerName,
        } as any)
        .select()
        .single();
      if (forkError) throw forkError;

      // e. Copy messages into new conversation
      if (messagesToCopy.length > 0) {
        const copies = messagesToCopy.map((m) => ({
          conversation_id: forked.id,
          role: m.role,
          content: m.content,
          ...(m.metadata && Object.keys(m.metadata).length > 0 ? { metadata: m.metadata } : {}),
        } as any));
        const { error: insertError } = await supabase.from('messages').insert(copies);
        if (insertError) throw insertError;
      }

      return forked as Conversation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}
